import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useEffect, useRef } from 'react'
import { useAgents } from '../../hooks/agents/useAgents'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { KnowledgeSource, MessageThread, UploadedAsset } from '../../types/chatTypes'
import ChatBubble from '../ChatBubble'
import { QuestionInput } from '../QuestionInput'
import { AgentWatermark } from './AgentWatermark'

const CHAT_SPACING = {
  horizontal: [2, 2, 16, 32, 48, 64],
}

interface ChatInterfaceProps {
  conversation: MessageThread[]
  chatContainerRef: React.RefObject<HTMLDivElement>
  isInputDisabled: boolean
  isTyping: boolean // To pass to QuestionInput or for other UI elements if needed
  onSend: (message: string, attachedDocument?: UploadedAsset) => void
  onRetry: (question: string, tempId: string, searchKnowledgeSource?: KnowledgeSource) => void
  onTypingChange: (isTyping: boolean) => void
  hasDocumentsInConversation: boolean // Prop to indicate if conversation contains documents
  onToggleResponseMinimized?: (threadTempId: string, responseMessageId: string) => void
  currentPhase?: { tempId: string; phase: 'internal_streaming' | 'llm_resumed' } | null
  isStreaming?: boolean // Track if any response is currently streaming
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  conversation,
  chatContainerRef,
  isInputDisabled,
  onSend,
  onRetry,
  onTypingChange,
  hasDocumentsInConversation,
  onToggleResponseMinimized,
  currentPhase,
  isStreaming,
}) => {
  const { t } = useLanguage()
  const { currentAgent } = useAgents()

  // Color mode values
  const textMutedColor = useColorModeValue('gray.500', 'gray.400')
  const inputContainerBg = useColorModeValue('white', 'gray.800')

  const SmartScrollToBottom = () => {
    const elementRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      // Only scroll if we're streaming or if this is the initial render
      if (elementRef.current && (isStreaming || conversation.length === 0)) {
        elementRef.current.scrollIntoView({ behavior: 'smooth' })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStreaming, conversation.length])

    return <div ref={elementRef} />
  }

  // Handler for sending messages that includes the current tool
  const handleSendMessage = (message: string, attachedDocument?: UploadedAsset) => {
    onSend(message, attachedDocument)
  }

  // Handler for retrying messages that includes the current tool
  const handleRetry = (question: string, tempId: string) => {
    onRetry(question, tempId)
  }

  // Handler for example question clicks
  const handleExampleQuestionClick = (question: string) => {
    handleSendMessage(question)
  }

  return (
    <Flex h='100%' flexDir='column'>
      <Flex
        ref={chatContainerRef}
        flex={1}
        minHeight='0'
        overflowY='scroll'
        overflowX='hidden'
        flexDirection='column'
      >
        <Box px={CHAT_SPACING.horizontal} py={6} h='100%'>
          {conversation.length === 0 && currentAgent ? (
            <AgentWatermark agent={currentAgent} onQuestionClick={handleExampleQuestionClick} />
          ) : (
            conversation.map(thread => {
              return (
                <ChatBubble
                  key={thread.tempId}
                  thread={thread}
                  onRetry={() => {
                    if (thread.question) {
                      handleRetry(thread.question, thread.tempId)
                    }
                  }}
                  onToggleResponseMinimized={
                    onToggleResponseMinimized
                      ? responseMessageId => onToggleResponseMinimized(thread.tempId, responseMessageId)
                      : undefined
                  }
                  currentPhase={currentPhase}
                />
              )
            })
          )}
          <SmartScrollToBottom />
        </Box>
      </Flex>
      <Box
        display='flex'
        flexDirection='column'
        position='sticky'
        bottom='0'
        py={2}
        bg={inputContainerBg}
        px={CHAT_SPACING.horizontal}
      >
        <Box display='flex' justifyContent='center'>
          <Box w='100%' maxW='768px'>
            <Box>
              <QuestionInput
                clearOnSend
                placeholder={t('chat.placeholder')}
                disabled={isInputDisabled}
                onTyping={() => onTypingChange(true)}
                onStopTyping={() => onTypingChange(false)}
                onSend={handleSendMessage}
                hasDocumentsInConversation={hasDocumentsInConversation}
              />
            </Box>
          </Box>
        </Box>
        <Box pb={4} />
        <Text fontSize='xs' color={textMutedColor} textAlign='center'>
          {t('chat.disclaimerText')}
        </Text>
      </Box>
    </Flex>
  )
}
